import { useParams, Link } from "react-router-dom";
import { CopyCode } from "@/components/CopyCode";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { CheckCircle2, XCircle, Clock, Ban, Trophy, AlertTriangle, ArrowLeft, Ticket as TicketIcon } from "lucide-react";
import { API_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatBs, formatUsd, formatDate } from "@/lib/format";
import { ConnectLogo } from "@/components/brand/ConnectLogo";

const STATUS_META = {
  active: {
    label: "Activo",
    hint: "Esperando resultado del sorteo",
    Icon: Clock,
    color: "text-sky-300",
    bg: "bg-sky-500/10 border-sky-500/30",
  },
  winner: {
    label: "Ganador",
    hint: "¡Ticket premiado! Preséntalo en la taquilla para cobrar",
    Icon: Trophy,
    color: "text-amber-300",
    bg: "bg-amber-500/10 border-amber-500/30",
  },
  paid: {
    label: "Pagado",
    hint: "Premio ya entregado al apostador",
    Icon: CheckCircle2,
    color: "text-emerald-300",
    bg: "bg-emerald-500/10 border-emerald-500/30",
  },
  lost: {
    label: "Sin premio",
    hint: "Este ticket no resultó ganador",
    Icon: XCircle,
    color: "text-slate-300",
    bg: "bg-slate-500/10 border-slate-500/30",
  },
  void: {
    label: "Anulado",
    hint: "Ticket anulado en taquilla — sin validez",
    Icon: Ban,
    color: "text-rose-300",
    bg: "bg-rose-500/10 border-rose-500/30",
  },
};

export default function PublicValidatorPage() {
  const { code } = useParams();
  const normalized = String(code || "").toUpperCase();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["public-ticket", normalized],
    queryFn: async () => (await axios.get(`${API_URL}/public/tickets/${normalized}`)).data,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-validator-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <ConnectLogo height={36} className="rounded-md" />
          <div className="flex-1" />
          <Link to="/login">
            <Button variant="outline" size="sm" data-testid="public-validator-login-link">
              <ArrowLeft size={14} className="mr-1.5" /> Ingresar
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            Consulta de ticket
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Serial <span className="font-mono text-primary" data-testid="public-ticket-code"><CopyCode code={normalized} /></span>
          </p>
        </div>

        {isLoading && (
          <Card className="p-6 bg-card border-border text-center text-muted-foreground" data-testid="public-ticket-loading">
            Consultando ticket…
          </Card>
        )}

        {isError && (
          <Card className="p-6 bg-card border-border text-center" data-testid="public-ticket-not-found">
            <div className="flex flex-col items-center gap-2">
              <AlertTriangle className="text-rose-300" size={28} />
              <div className="font-display text-lg">Ticket no encontrado</div>
              <p className="text-sm text-muted-foreground max-w-md">
                Verifica que el serial esté escrito correctamente. Si escaneaste el QR desde el
                recibo, asegúrate de estar leyendo un ticket emitido en este sistema.
              </p>
              <div className="text-xs text-muted-foreground mt-2">
                {error?.response?.status ? `HTTP ${error.response.status}` : "Error de red"}
              </div>
            </div>
          </Card>
        )}

        {data && <TicketPanel data={data} />}

        <p className="text-[11px] text-muted-foreground text-center pt-4">
          Los datos se muestran únicamente para verificación del apostador. No incluyen datos personales.
        </p>
      </main>
    </div>
  );
}

function TicketPanel({ data }) {
  const meta = STATUS_META[data.status] || STATUS_META.active;
  const Icon = meta.Icon;
  return (
    <>
      <Card className={`p-5 border ${meta.bg}`} data-testid={`public-ticket-status-${data.status}`}>
        <div className="flex items-center gap-3">
          <Icon size={40} className={meta.color} strokeWidth={1.75} />
          <div className="flex-1">
            <div className={`font-display text-2xl font-semibold ${meta.color}`}>{meta.label}</div>
            <div className="text-sm text-muted-foreground">{meta.hint}</div>
          </div>
          {data.status === "winner" && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-amber-300/80">Premio</div>
              <div className="font-display text-xl text-amber-300 tabular-nums">
                {formatBs(data.payout_total_bs)} Bs
              </div>
              <div className="text-xs text-amber-300/80 tabular-nums">$ {formatUsd(data.payout_total_usd)}</div>
            </div>
          )}
          {data.status === "paid" && (
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-emerald-300/80">Entregado</div>
              <div className="font-display text-xl text-emerald-300 tabular-nums">
                {formatBs(data.payout_net_bs || data.payout_total_bs)} Bs
              </div>
              <div className="text-[11px] text-emerald-300/70">{formatDate(data.paid_at)}</div>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-5 bg-card border-border space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground uppercase text-[11px] tracking-widest">Emitido</div>
          <div className="tabular-nums" data-testid="public-ticket-created">{formatDate(data.created_at)}</div>
        </div>
        {(data.taquilla_name || data.banca_name) && (
          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground uppercase text-[11px] tracking-widest">Taquilla</div>
            <div>{data.taquilla_name}{data.banca_name ? <span className="text-muted-foreground"> · {data.banca_name}</span> : null}</div>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground uppercase text-[11px] tracking-widest">Total</div>
          <div className="tabular-nums font-mono">
            <span className="text-foreground">{formatBs(data.total_bs)} Bs</span>
            <span className="text-muted-foreground"> · $ {formatUsd(data.total_usd)}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground uppercase text-[11px] tracking-widest">Tasa BCV</div>
          <div className="tabular-nums font-mono">{formatBs(data.exchange_rate)} Bs/USD</div>
        </div>
      </Card>

      <Card className="p-4 bg-card border-border">
        <div className="flex items-center gap-2 mb-3">
          <TicketIcon size={16} className="text-primary" />
          <div className="font-display font-semibold">Jugadas ({data.plays.length})</div>
        </div>
        <Separator className="mb-3" />
        <ul className="space-y-2" data-testid="public-ticket-plays">
          {data.plays.map((p, i) => (
            <li
              key={i}
              className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 border ${
                p.is_winner ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-secondary/40"
              }`}
              data-testid={`public-ticket-play-${i}`}
            >
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-medium">
                  {p.lottery_name}
                  {p.sorteo_label ? <span className="text-muted-foreground"> · {p.sorteo_label}</span> : null}
                </div>
                <div className="text-[11px] text-muted-foreground">{p.sorteo_scheduled_at ? formatDate(p.sorteo_scheduled_at) : ""}</div>
              </div>
              <Badge variant="outline" className="font-mono text-base tabular-nums border-primary/40 text-primary">
                {p.number}
              </Badge>
              {p.sign && (
                <Badge variant="outline" className="border-border/60 text-muted-foreground">{p.sign}</Badge>
              )}
              <div className="text-sm tabular-nums font-mono min-w-[100px] text-right">
                {formatBs(p.amount_bs)} Bs
              </div>
              {p.is_winner && (
                <div className="text-sm tabular-nums font-mono min-w-[110px] text-right text-amber-300">
                  + {formatBs(p.payout_bs)} Bs
                </div>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
